import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Loader2, AlertTriangle, Coins } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const Chip = ({ children, tono = "gris" }) => {
  const t = {
    gris: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
    ok: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    alerta: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    rojo: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  }[tono];
  return <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${t}`}>{children}</span>;
};

export const CierreRatiosTab = ({ desde, hasta }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [todo, setTodo] = useState(true);

  const cargar = useCallback(async (rango) => {
    setLoading(true);
    try {
      const params = rango ? { desde, hasta } : {};
      const r = await axios.get(`${API}/superunion/cierre/ratios`, { params, ...auth() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron leer los ratios");
    } finally { setLoading(false); }
  }, [desde, hasta]);

  useEffect(() => { cargar(false); }, []);       // eslint-disable-line

  const uniones = data?.uniones || [];
  const cambiaron = uniones.filter((u) => u.coincide === false);

  return (
    <div className="space-y-4" data-testid="cierre-ratios">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-start gap-2">
          <Coins className="w-5 h-5 text-indigo-500 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Ratios de fichas por unión</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-3xl">
              Sale del encabezado de cada cuadro de la primera pestaña del Excel («Unión 1534 Ratio de fichas 1:5»).
              Es sólo referencia: el balance del cierre siempre usa el bloque 1:1 en USD. Cada vez que abrís esta
              pestaña los tramos quedan guardados con su rango de fechas.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-gray-500">
            <input type="checkbox" checked={todo} data-testid="cierre-ratios-todo"
              onChange={(e) => { setTodo(e.target.checked); cargar(!e.target.checked); }} />
            Todo el historial
          </label>
          <button onClick={() => cargar(!todo)} disabled={loading} data-testid="cierre-ratios-refrescar"
            className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Releer
          </button>
        </div>
      </div>

      {!!cambiaron.length && (
        <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-lg p-3"
          data-testid="cierre-ratios-alerta">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
          <p className="text-xs text-amber-800 dark:text-amber-200">
            {cambiaron.length} unión/es tienen distinto ratio en PPSR y PPST:{" "}
            <strong>{cambiaron.map((u) => `${u.nombre || u.ambito_id} (${u.ambito_id}): PPSR ${u.ppsr?.ratio} · PPST ${u.ppst?.ratio}`).join(" · ")}</strong>.
            Es justo la diferencia que deja margen en divisa: revisá que sea a propósito.
          </p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm" data-testid="cierre-ratios-tabla">
          <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500">
            <tr>
              {["Tipo", "ID", "Nombre", "Ratio PPSR", "Ratio PPST", "1 USD =", "Vigente", "En el cierre"].map((h) => (
                <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>))}
            </tr>
          </thead>
          <tbody className="divide-y dark:divide-gray-700">
            {uniones.map((u) => (
              <tr key={`${u.ambito}-${u.ambito_id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/40"
                data-testid={`cierre-ratio-${u.ambito_id}`}>
                <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                  {u.ambito === "union" ? "Unión" : "Super Unión"}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{u.ambito_id}</td>
                <td className="px-3 py-2 whitespace-nowrap">{u.nombre || <span className="text-gray-400">sin nombre</span>}</td>
                <td className="px-3 py-2"><Chip tono={u.coincide === false ? "alerta" : "gris"}>{u.ppsr?.ratio || "-"}</Chip></td>
                <td className="px-3 py-2"><Chip tono={u.coincide === false ? "alerta" : "gris"}>{u.ppst?.ratio || "-"}</Chip></td>
                <td className="px-3 py-2 tabular-nums text-xs text-gray-600 dark:text-gray-300">
                  {u.ppst?.divisor || u.ppsr?.divisor || "-"} fichas
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                  {(u.ppst || u.ppsr)?.desde} → {(u.ppst || u.ppsr)?.hasta}
                </td>
                <td className="px-3 py-2">
                  {u.ambito !== "union" ? <Chip>—</Chip>
                    : u.en_config ? <Chip tono="ok">sí</Chip> : <Chip tono="rojo">no configurada</Chip>}
                </td>
              </tr>
            ))}
            {!uniones.length && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                {loading ? "Leyendo los imports..." : "No hay imports con bloques de ratio"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-x-auto">
        <p className="px-3 pt-3 text-xs font-semibold text-gray-500">
          Historial de tramos guardados ({(data?.tramos || []).length})
        </p>
        <table className="w-full text-xs" data-testid="cierre-ratios-tramos">
          <thead className="text-gray-500">
            <tr>{["Desde", "Hasta", "Días", "Tipo", "Dueño", "Ratio"].map((h) => (
              <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>))}</tr>
          </thead>
          <tbody className="divide-y dark:divide-gray-700">
            {(data?.tramos || []).map((t, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 whitespace-nowrap">{t.desde}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{t.hasta}</td>
                <td className="px-3 py-1.5 tabular-nums">{t.dias}</td>
                <td className="px-3 py-1.5">{t.tipo}</td>
                <td className="px-3 py-1.5 font-mono">{t.ambito === "union" ? "U" : "SU"} {t.ambito_id}</td>
                <td className="px-3 py-1.5 font-semibold">{t.ratio}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CierreRatiosTab;
